import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { importLibrary } from '@googlemaps/js-api-loader'

import type { JobCandidate, RealtimeDataSource } from '../api/data-source'
import { realtimeConfig } from '../config'
import { CANDIDATE_RECOMPUTE_MS } from '../schedulingRules'
import type { Job, Worker } from '../types'
import { hasValidCoords } from '../utils/distance'
import { findNearbyCandidates, mergeServerCandidates, type NearbyCandidate } from '../utils/nearby-workers'

export type CandidatesStatus = 'idle' | 'loading' | 'ready' | 'error'

const DISTANCE_MATRIX_LIMIT = 10

/** Returns `value`, updated at most once per `ms` (leading + trailing). */
export function useThrottledValue<T>(value: T, ms: number): T {
  const [throttled, setThrottled] = useState(value)
  const lastRef = useRef(0)
  useEffect(() => {
    const elapsed = Date.now() - lastRef.current
    if (elapsed >= ms) {
      lastRef.current = Date.now()
      setThrottled(value)
      return undefined
    }
    const timer = setTimeout(() => {
      lastRef.current = Date.now()
      setThrottled(value)
    }, ms - elapsed)
    return () => clearTimeout(timer)
  }, [ms, value])
  return throttled
}

/** Driving ETAs for the nearest candidates via Distance Matrix (opt-in; failures fall back silently). */
async function fetchDrivingEtas(job: Job, candidates: NearbyCandidate[]): Promise<Map<string, number>> {
  const etas = new Map<string, number>()
  if (!hasValidCoords(job) || typeof window === 'undefined' || !window.google?.maps) return etas
  const origins = candidates.slice(0, DISTANCE_MATRIX_LIMIT)
  if (origins.length === 0) return etas
  await importLibrary('routes')
  const response = await new google.maps.DistanceMatrixService().getDistanceMatrix({
    origins: origins.map(({ worker }) => ({ lat: worker.lat as number, lng: worker.lng as number })),
    destinations: [{ lat: job.lat, lng: job.lng }],
    travelMode: google.maps.TravelMode.DRIVING,
  })
  response.rows.forEach((row, index) => {
    const seconds = row.elements[0]?.duration?.value
    if (typeof seconds === 'number') etas.set(origins[index].worker.id, Math.max(1, Math.round(seconds / 60)))
  })
  return etas
}

/**
 * Nearby workers for the selected job (strip + candidate pins + fitBounds).
 *
 * With a candidates endpoint the server decides who is eligible and the list reloads when the
 * job or radius changes; without one (current backend) it is ranked here from the realtime
 * snapshot. Either way live data refines it: a worker who goes offline/busy or is added to the
 * job drops out, and distances follow location events (recomputed at most every 3 s).
 */
export function useNearbyCandidates({
  source,
  job,
  workers,
  jobs,
  radiusKm,
  enabled,
}: {
  source: RealtimeDataSource
  job: Job | null
  workers: Worker[]
  jobs: Job[]
  radiusKm: number
  enabled: boolean
}) {
  const [status, setStatus] = useState<CandidatesStatus>('idle')
  const [serverCandidates, setServerCandidates] = useState<Map<string, JobCandidate> | null>(null)
  const [etaOverrides, setEtaOverrides] = useState<Map<string, number> | undefined>(undefined)
  const [attempt, setAttempt] = useState(0)
  const liveWorkers = useThrottledValue(workers, CANDIDATE_RECOMPUTE_MS)
  const jobRef = useRef(job)
  jobRef.current = job
  const jobId = enabled ? job?.id ?? null : null
  const jobLocated = hasValidCoords(job)

  useEffect(() => {
    setServerCandidates(null)
    setEtaOverrides(undefined)
    if (!jobId) {
      setStatus('idle')
      return undefined
    }
    if (!source.fetchCandidates || !jobLocated) {
      setStatus('ready')
      return undefined
    }
    let cancelled = false
    setStatus('loading')
    source.fetchCandidates({ job: jobRef.current as Job, radiusKm })
      .then((list) => {
        if (cancelled) return
        setServerCandidates(new Map(list.map((candidate) => [candidate.workerId, candidate])))
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [attempt, jobId, jobLocated, radiusKm, source])

  const candidates = useMemo(() => {
    if (!job || !jobId || status !== 'ready') return []
    const local = findNearbyCandidates(job, liveWorkers, jobs, { radiusKm, etaOverrides })
    return serverCandidates ? mergeServerCandidates(local, serverCandidates, etaOverrides) : local
  }, [etaOverrides, job, jobId, jobs, liveWorkers, radiusKm, serverCandidates, status])

  // Optional driving ETAs, fetched once per job + radius.
  const etaRequestedFor = useRef<string | null>(null)
  const candidatesRef = useRef(candidates)
  candidatesRef.current = candidates
  useEffect(() => {
    const key = jobId ? `${jobId}|${radiusKm}` : null
    if (!realtimeConfig.useDistanceMatrix || status !== 'ready' || !key || etaRequestedFor.current === key) return
    const current = jobRef.current
    if (!current || candidatesRef.current.length === 0) return
    etaRequestedFor.current = key
    fetchDrivingEtas(current, candidatesRef.current)
      .then((etas) => {
        if (etas.size > 0 && jobRef.current?.id === jobId) setEtaOverrides(etas)
      })
      .catch(() => undefined)
  }, [candidates.length, jobId, radiusKm, status])

  const retry = useCallback(() => setAttempt((value) => value + 1), [])

  return { status, candidates, retry, jobLocated }
}
